// The console's data alarms and unknown devices (device-type-console WEB-DTC-10 … WEB-DTC-14, VisionPlatform
// device-type-console DTC-30 … DTC-35, DTC-40 … DTC-44) over vision-api 0.44.0's listDataAlarms, closeDataAlarm,
// listUnknownDevices and actOnUnknownDevice.
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../../api/client";
import type { components } from "../../../api/generated/vision-api";
import type { DataAlarmFilter } from "./filter";

export type { DataAlarmFilter, DataAlarmState } from "./filter";

export type DataAlarm = components["schemas"]["DataAlarm"];
export type UnknownDevice = components["schemas"]["UnknownDevice"];
export type UnknownState = "warning" | "ignored" | "imported" | "all";
export type UnknownFilter = { state: UnknownState; network?: "real" | "virtual" };
export type UnknownAction = "ignore" | "unignore" | "forget";

const ops = () => apiClient("ops");
/** The menu's counts refresh every minute while the console is open (DTC-33, DTC-42). */
const COUNT_EVERY = 60_000;

export const dataAlarmsKey = ["ops", "/v1/ops/data-alarms"] as const;
export const unknownDevicesKey = ["ops", "/v1/ops/unknown-devices"] as const;

export const dataAlarmsQuery = (f: DataAlarmFilter) =>
  infiniteQueryOptions({
    queryKey: [...dataAlarmsKey, f],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(ops().GET("/v1/ops/data-alarms", { params: { query: { ...f, cursor: pageParam, limit: 100 } } })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** Open data alarms on the server, for the menu (DTC-33). */
export const openDataAlarmsQuery = () =>
  queryOptions({
    queryKey: [...dataAlarmsKey, "count"],
    queryFn: () => unwrap(ops().GET("/v1/ops/data-alarms", { params: { query: { state: "open", limit: 1 } } })),
    select: (page) => page.open,
    refetchInterval: COUNT_EVERY,
  });

export function closeDataAlarm(id: string, note: string): Promise<DataAlarm> {
  return unwrap(
    ops().POST("/v1/ops/data-alarms/{alarm_id}/close", { params: { path: { alarm_id: id } }, body: { note } }),
  );
}

export const unknownDevicesQuery = (f: UnknownFilter) =>
  infiniteQueryOptions({
    queryKey: [...unknownDevicesKey, f],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(ops().GET("/v1/ops/unknown-devices", { params: { query: { ...f, cursor: pageParam, limit: 100 } } })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** Unknown devices in warning heard in the last 24 h from a real network, for the menu and home (DTC-42). */
export const unknownWarningsQuery = () =>
  queryOptions({
    queryKey: [...unknownDevicesKey, "count"],
    queryFn: () => unwrap(ops().GET("/v1/ops/unknown-devices", { params: { query: { limit: 1 } } })),
    select: (page) => page.warnings_24h,
    refetchInterval: COUNT_EVERY,
  });

export async function actOnUnknown(id: string, action: UnknownAction): Promise<void> {
  await unwrap(
    ops().POST("/v1/ops/unknown-devices/{unknown_id}/{action}", { params: { path: { unknown_id: id, action } } }),
  );
}

/** The type's product, for the capability scope: a type id is `<pid>` or `<pid>.<fid>`. */
export function productOf(typeId: string): string {
  return typeId.split(".")[0] ?? typeId;
}

/** Whether the person may close a data alarm on this type (DTC-61): `debug.request` for all products or this one. */
export function canCloseDataAlarm(
  grants: readonly { capability: string; products?: string[] }[] | undefined,
  typeId: string,
): boolean {
  const p = productOf(typeId);
  return (grants ?? []).some(
    (g) => g.capability === "debug.request" && (!g.products?.length || g.products.includes(p)),
  );
}

/** A reading with its unit, as the alarm shows it: "612 °C". */
export function withUnit(v: number, unit: string | null | undefined, lang: string): string {
  const n = new Intl.NumberFormat(lang, { maximumFractionDigits: 3 }).format(v);
  return unit ? `${n} ${unit}` : n;
}

/** The address a frame came from: "45.155.0.1:5683", or the address alone. */
export function address(ip: string | null | undefined, port: number | null | undefined): string {
  if (!ip) return "—";
  const host = ip.includes(":") ? `[${ip}]` : ip;
  return port ? `${host}:${port}` : ip;
}
